import { useCallback, useEffect, useState } from "react";
import { useClinic, useSubscription, useTracker } from "@/contexts";
import useToast from "./useToast";
import { EVENTS } from "@/utils/tracker";

interface PaywallOptions {
  delay?: number;
  source?: string;
  action?: string;
}

/**
 * Custom hook for managing paywall functionality.
 *
 * This hook handles the display and behavior of paywall screens, including subscription checks,
 * clinic validation, and analytics tracking. It automatically hides the paywall when the user
 * has an active subscription.
 *
 * @returns Object containing paywall state and control methods
 * @returns showPaywall - Whether the paywall should be displayed
 * @returns onOpenPaywall - Function to open the paywall with optional configuration
 * @returns onClosePaywall - Function to close the paywall
 */
const usePaywall = () => {
  const [showPaywall, setShowPaywall] = useState(false);
  const { isClinic, selectedClinic: clinic } = useClinic();
  const { isSubscribed } = useSubscription();
  const displayToast = useToast();
  const { captureEvent } = useTracker();

  const onOpenPaywall = useCallback((options?: PaywallOptions | number) => {
    const delay = typeof options === "number" ? options : options?.delay;
    const source = typeof options === "object" ? options.source : undefined;
    const action = typeof options === "object" ? options.action : undefined;

    if (!isClinic) {
      displayToast({
        message:
          "Your clinic's subscription has expired. Please contact your clinic owner or administrator to renew the subscription.",
        duration: 5000,
        position: "bottom",
        positionAnchor: "tab-bar",
      });
      return;
    }

    if (clinic?.id) {
      captureEvent({
        event: EVENTS.PAYWALL,
        properties: {
          clinic_id: clinic.id,
          ...(source && { source }),
          ...(action && { action }),
        },
      });
    }

    if (delay && delay > 0) {
      setTimeout(() => {
        setShowPaywall(true);
      }, delay);
    } else {
      setShowPaywall(true);
    }
  }, [isClinic, displayToast, clinic?.id, captureEvent]);

  const onClosePaywall = useCallback(() => {
    setShowPaywall(false);
  }, []);

  useEffect(() => {
    if (isSubscribed) {
      setShowPaywall(false);
    }
  }, [isSubscribed]);

  return {
    showPaywall,
    onOpenPaywall,
    onClosePaywall,
  };
};

export default usePaywall;
